import Popup from "@/components/shared/Popup";
import { useState, useMemo, useEffect } from "react";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiEdit,
  FiEye,
  FiFileText,
  FiInfo,
  FiLink,
  FiLink2,
  FiLock,
} from "react-icons/fi";
import { Link, useParams, useRouteContext } from "@tanstack/react-router";
import { useLeaData, useFileSubmissionsData } from "@/queries";
import { resolveQuickSearchRoute } from "@/hooks/useQuickSearchNavigate";
import { CreateRecordPopup } from "./CreateRecordPopup";
import {
  DiscrepancyDetails,
  getEntityIdLabel,
  getEntityIdSearchType,
  useDiscrepancyPopup,
} from "./hooks/useDiscrepancyPopup";
import { useRecordPermissions } from "./hooks/useRecordPermissions";
import { AllErrorsTab } from "./Tabs/AllErrorsTab";
import { RecordDataTable } from "./Tabs/RecordDataTable";
import { RelatedRecordsTab } from "./Tabs/RelatedRecordsTab";
import type { CLEN, ENRL, LANG, SCLR, SLNG } from "@/models";
import type { CHLD } from "@/models/chld";
import { FAMI } from "@/models/fami";
import type { SASS, STFF } from "@/models/stff";
import type { CLRM } from "@/models/clrm";
import { getFreshRecordData } from "@/utils/getFreshRecordData";

import { FileStatusEnum } from "@/models/file-submission";

export type Tab = "recordData" | "allErrors" | "relatedRecords";
type ChildRecordType = "CHLD" | "ENRL" | "CLEN" | "FAMI" | "LANG" | "SLNG";
type StaffRecordType = "STFF" | "SASS" | "SCLR";
type ClassroomRecordType = "CLRM";
type RecordType = ChildRecordType | StaffRecordType | ClassroomRecordType;
type RecordDataMap = {
  CHLD: CHLD;
  ENRL: ENRL;
  CLEN: CLEN;
  FAMI: FAMI;
  LANG: LANG;
  SLNG: SLNG;
  STFF: STFF;
  SASS: SASS;
  SCLR: SCLR;
  CLRM: CLRM;
};

export type UpdateRecordParams<T extends RecordType = RecordType> = {
  recordType: T;
  recordData: T extends keyof RecordDataMap
    ? RecordDataMap[T]
    : Record<string, unknown>;
  entityId: string | undefined;
  sourceTab?: Tab;
};

type DiscrepancyPopupProps = {
  discrepancy: DiscrepancyDetails;
  onClose: () => void;
  errorMessageMap: Map<string, any>;
  onUpdateRecord?: (params: UpdateRecordParams) => void;
  initialTab?: Tab;
  anomalyRunEndTime?: string;
  title?: string;
  titleIcon?: React.ReactNode;
};

export function NoAccessMessage({ recordType }: { recordType: string }) {
  return (
    <div className="discrepancy-no-access">
      <div className="d-flex align-items-center gap-2 text-muted">
        <FiLock size={18} />
        <span>No access for {recordType} records</span>
      </div>
    </div>
  );
}

type FreshnessStatus =
  | { type: "updated"; date: Date }
  | { type: "pending"; date: Date }
  | { type: "current"; date: Date | null };

function useDataFreshness(
  recordType: string,
  anomalyRunEndTime: string | undefined,
  fileSubmissions: any[] | undefined,
  freshRecord: any
): FreshnessStatus {
  return useMemo(() => {
    const runEndDate = anomalyRunEndTime ? new Date(anomalyRunEndTime) : null;

    if (!runEndDate || !fileSubmissions) {
      return { type: "current", date: null };
    }

    const typeSubmissions = fileSubmissions.filter((fs: any) => {
      const matchesType = fs.RecordTypeCode === recordType;
      const notRejected =
        fs.CurrentStatus !== FileStatusEnum.Rejected &&
        fs.CurrentStatus !== FileStatusEnum.Failed;
      return matchesType && notRejected;
    });

    const newerSubmissions = typeSubmissions.filter(
      (fs: any) => new Date(fs.CreateDate) > runEndDate
    );

    if (newerSubmissions.length > 0) {
      const sorted = [...newerSubmissions].sort(
        (a: any, b: any) =>
          new Date(b.CreateDate).getTime() - new Date(a.CreateDate).getTime()
      );
      const newest = sorted[0];
      const newestDate = new Date(newest.CreateDate);

      if (newest.CurrentStatus === FileStatusEnum.Posted) {
        return { type: "updated", date: newestDate };
      }

      return { type: "pending", date: newestDate };
    }

    const fileSubId = freshRecord?.FileSubmissionId;
    const recordSubmission = fileSubId
      ? fileSubmissions.find((fs: any) => fs.Id === fileSubId)
      : null;

    if (recordSubmission) {
      return { type: "current", date: new Date(recordSubmission.CreateDate) };
    }

    const lastPosted = typeSubmissions
      .filter((fs: any) => fs.CurrentStatus === FileStatusEnum.Posted)
      .sort(
        (a: any, b: any) =>
          new Date(b.CreateDate).getTime() - new Date(a.CreateDate).getTime()
      )[0];

    return {
      type: "current",
      date: lastPosted ? new Date(lastPosted.CreateDate) : null,
    };
  }, [recordType, anomalyRunEndTime, fileSubmissions, freshRecord]);
}

function formatBannerDate(date: Date): string {
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function DataFreshnessBanner({ status }: { status: FreshnessStatus }) {
  const icons = {
    updated: <FiCheckCircle size={16} />,
    pending: <FiClock size={16} />,
    current: <FiClock size={16} />,
  };

  let message: string;
  switch (status.type) {
    case "updated":
      message = `Record updated ${formatBannerDate(status.date)} (after last check)`;
      break;
    case "pending":
      message = `File submitted ${formatBannerDate(status.date)} — pending next discrepancy check`;
      break;
    case "current":
      message = status.date
        ? `Data as of last check — last modified ${formatBannerDate(status.date)}`
        : "Data as of last discrepancy check";
      break;
  }

  return (
    <div
      className={`discrepancy-freshness-banner discrepancy-freshness-banner--${status.type}`}
    >
      {icons[status.type]}
      <span>{message}</span>
    </div>
  );
}

type EffectiveDated = {
  EffectiveStartDate?: string;
  EffectiveEndDate?: string | null;
};

function byMostRecentStart(a: EffectiveDated, b: EffectiveDated): number {
  return (b.EffectiveStartDate ?? "").localeCompare(a.EffectiveStartDate ?? "");
}

function pickCurrentRecord<T extends EffectiveDated>(
  records: T[]
): T | undefined {
  const openEnded = records.filter((r) => !r.EffectiveEndDate);
  return (
    [...openEnded].sort(byMostRecentStart)[0] ??
    [...records].sort(byMostRecentStart)[0]
  );
}

export function DiscrepancyPopup({
  discrepancy,
  onClose,
  errorMessageMap,
  onUpdateRecord,
  initialTab = "recordData",
  anomalyRunEndTime,
  title = "Data Discrepancy",
  titleIcon = <FiAlertTriangle size={20} />,
}: DiscrepancyPopupProps) {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [relatedRecordsDetailView, setRelatedRecordsDetailView] =
    useState(false);
  const [relatedRecordsBackHandler, setRelatedRecordsBackHandler] = useState<
    (() => void) | null
  >(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleRelatedRecordsDetailViewChange = (
    isDetailView: boolean,
    onBack?: () => void
  ) => {
    setRelatedRecordsDetailView(isDetailView);
    setRelatedRecordsBackHandler(isDetailView && onBack ? () => onBack : null);
  };

  const leaContext = useRouteContext({ from: "/$leaid" });
  const { coreData, currentOrgPreschools, currentUserOrg } = leaContext;
  const { data: leaData } = useLeaData(currentUserOrg.esiIdPath);
  const { viewPermissions, editPermissions, canViewRecord, canEditRecord } =
    useRecordPermissions();
  const { data: fileSubmissions } = useFileSubmissionsData(
    currentUserOrg.esiIdPath
  );

  const leaName = currentUserOrg.orgName;

  const {
    anomaly,
    referenceRecord,
    errorDetails,
    relatedErrors,
    viewMode,
    missingRecordType,
    entityId,
    affectedFields,
    recordTypeName,
    errorsByRecordType,
    handleBackToError,
    getDefaultsForMissingRecord,
  } = useDiscrepancyPopup(discrepancy, onClose);

  const entityIdLabel = getEntityIdLabel(anomaly);
  const entityIdSearchType = getEntityIdSearchType(anomaly);
  const canViewPrimaryRecord = canViewRecord(anomaly.ReferenceRecordType);
  const canEditPrimaryRecord = canEditRecord(anomaly.ReferenceRecordType);

  const { leaid } = useParams({ from: "/$leaid" });

  const entityRoute = useMemo(() => {
    if (!entityId || !entityIdSearchType) return;
    return resolveQuickSearchRoute(leaData, leaid, entityIdSearchType, entityId);
  }, [leaData, leaid, entityIdSearchType, entityId]);

  const canNavigateToEntityId =
    canViewPrimaryRecord && !!entityId && !!entityRoute?.to;

  const freshReferenceRecord = useMemo(() => {
    return getFreshRecordData(
      leaData,
      anomaly.ReferenceRecordType,
      referenceRecord,
      entityId
    );
  }, [leaData, anomaly.ReferenceRecordType, referenceRecord, entityId]);

  const entityName = useMemo(() => {
    if (!entityId) return;

    if (entityIdLabel === "CSPPID") {
      const matches = leaData?.chld?.filter((c) => c.CSPPID === entityId) ?? [];
      const child = pickCurrentRecord(matches);
      if (!child) return;
      return [child.ChildFirstName, child.ChildLastName]
        .filter(Boolean)
        .join(" ");
    }

    if (entityIdLabel === "Local Staff ID") {
      const matches =
        leaData?.stff?.filter(
          (s) => String(s.LocalStaffId) === String(entityId)
        ) ?? [];
      const staff = pickCurrentRecord(matches);
      if (!staff) return;
      return [staff.StaffLegalFirstName, staff.StaffLegalLastName]
        .filter(Boolean)
        .join(" ");
    }
  }, [leaData, entityIdLabel, entityId]);

  const entityTypeLabel =
    entityIdLabel === "CSPPID"
      ? "Child"
      : entityIdLabel === "Local Staff ID"
        ? "Staff"
        : undefined;

  const freshnessStatus = useDataFreshness(
    anomaly.ReferenceRecordType,
    anomalyRunEndTime,
    fileSubmissions,
    freshReferenceRecord
  );

  const handleUpdateClick = () => {
    if (onUpdateRecord && canViewPrimaryRecord) {
      onUpdateRecord({
        recordType: anomaly.ReferenceRecordType as RecordType,
        recordData: freshReferenceRecord as any,
        entityId,
        sourceTab: activeTab,
      });
    }
  };

  const handleSecondaryRecordClick = () => {
    if (onUpdateRecord && anomaly.DiscrepancyRecordType) {
      if (!canViewRecord(anomaly.DiscrepancyRecordType)) return;

      const recordType = anomaly.DiscrepancyRecordType as RecordType;

      if (recordType === "CLRM" && leaData) {
        const ref = referenceRecord as any;
        const csppid = ref?.CSPPID ?? entityId;
        const programYear = ref?.ProgramYear;
        const preschoolCode = ref?.PreschoolCode;

        const childRecord = leaData.chld?.find((c: any) => c.CSPPID === csppid);
        if (!childRecord) {
          alert("Cannot open Classroom record: Child record not found.");
          return;
        }

        const clenRecords = (childRecord as any)?.ClassroomAssignments ?? [];
        let matchingClen = clenRecords.find(
          (clen: any) =>
            clen.ProgramYear === programYear &&
            clen.PreschoolCode === preschoolCode
        );
        if (!matchingClen && clenRecords.length > 0)
          matchingClen = clenRecords[0];

        if (!matchingClen) {
          alert(
            "Cannot open Classroom record: This child does not have a Classroom Enrollment (CLEN) record."
          );
          return;
        }

        const localClassroomId = matchingClen.LocalClassroomId;
        const classrooms = leaData.entities?.classrooms ?? {};
        let foundClassroom = null;

        for (const key of Object.keys(classrooms)) {
          const classroomEntity = classrooms[key];
          const classroomRecords = classroomEntity?.records ?? [
            classroomEntity,
          ];
          for (const classroom of classroomRecords) {
            if (!classroom) continue;
            if (
              String(classroom.LocalClassroomId) === String(localClassroomId) &&
              classroom.ProgramYear === programYear &&
              classroom.PreschoolCode === preschoolCode
            ) {
              foundClassroom = classroom;
              break;
            }
          }
          if (foundClassroom) break;
        }

        if (!foundClassroom) {
          for (const key of Object.keys(classrooms)) {
            const classroomEntity = classrooms[key];
            const classroomRecords = classroomEntity?.records ?? [
              classroomEntity,
            ];
            for (const classroom of classroomRecords) {
              if (!classroom) continue;
              if (
                String(classroom.LocalClassroomId) ===
                  String(localClassroomId) &&
                classroom.ProgramYear === programYear
              ) {
                foundClassroom = classroom;
                break;
              }
            }
            if (foundClassroom) break;
          }
        }

        if (!foundClassroom) {
          for (const key of Object.keys(classrooms)) {
            const classroomEntity = classrooms[key];
            const classroomRecords = classroomEntity?.records ?? [
              classroomEntity,
            ];
            for (const classroom of classroomRecords) {
              if (!classroom) continue;
              if (
                String(classroom.LocalClassroomId) === String(localClassroomId)
              ) {
                foundClassroom = classroom;
                break;
              }
            }
            if (foundClassroom) break;
          }
        }

        if (!foundClassroom) {
          alert("Cannot open Classroom record: Classroom not found.");
          return;
        }

        onUpdateRecord({
          recordType,
          recordData: foundClassroom as any,
          entityId,
          sourceTab: activeTab,
        });
        return;
      }

      const freshRecordData = getFreshRecordData(
        leaData,
        recordType,
        referenceRecord,
        entityId
      );
      onUpdateRecord({
        recordType,
        recordData: freshRecordData as any,
        entityId,
        sourceTab: activeTab,
      });
    }
  };

  const handleFixRecord = (recordType: string) => {
    if (!canViewRecord(recordType) || !onUpdateRecord) return;
    const freshRecordData = getFreshRecordData(
      leaData,
      recordType,
      referenceRecord,
      entityId
    );
    onUpdateRecord({
      recordType: recordType as RecordType,
      recordData: freshRecordData as any,
      entityId,
      sourceTab: "allErrors",
    });
  };

  if (viewMode === "createRecord" && missingRecordType) {
    return (
      <CreateRecordPopup
        recordType={missingRecordType}
        defaults={getDefaultsForMissingRecord()}
        onClose={onClose}
        onBack={handleBackToError}
      />
    );
  }

  return (
    <Popup
      show={true}
      header={
        <div
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "1.25rem" }}
        >
          {titleIcon}
          <span>{title}</span>
        </div>
      }
      size="lg"
      onClose={onClose}
      hasButton={canViewPrimaryRecord}
      buttonText={
        <div className="d-flex align-items-center gap-2">
          {canEditPrimaryRecord ? <FiEdit size={16} /> : <FiEye size={16} />}
          <span>{canEditPrimaryRecord ? "Update Record" : "View Record"}</span>
        </div>
      }
      onButtonClick={handleUpdateClick}
      secondButtonText="Cancel"
      onSecondButtonClick={onClose}
      backButtonText={
        relatedRecordsDetailView ? (
          <div className="d-flex align-items-center gap-2">
            <FiArrowLeft size={16} />
            <span>Back</span>
          </div>
        ) : undefined
      }
      onBackButtonClick={
        relatedRecordsDetailView && relatedRecordsBackHandler
          ? relatedRecordsBackHandler
          : undefined
      }
      subHeader={<DataFreshnessBanner status={freshnessStatus} />}
    >
      <div className="discrepancy-popup-body">
        {/* Error Header Card */}
        <div className="discrepancy-error-card">
          <div className="discrepancy-error-card__body">
            <div className="discrepancy-error-card__header-row">
              <div className="discrepancy-error-card__icon">
                <FiAlertTriangle size={22} color="#DC2626" />
              </div>
              <div className="flex-grow-1">
                <div className="mb-1">
                  <span className="text-danger" style={{ fontWeight: 500 }}>
                    Error{" "}
                  </span>
                  <span className="text-danger" style={{ fontWeight: 700 }}>
                    {anomaly.ErrorCode}
                  </span>
                </div>
                <div className="text-dark">
                  {errorDetails?.ErrorMessageText ?? "--"}
                </div>
              </div>
              <div className="discrepancy-error-card__entity-card">
                {entityName && (
                  <div className="discrepancy-error-card__entity-row">
                    <span className="discrepancy-error-card__entity-label">
                      {entityTypeLabel}:
                    </span>
                    <span className="discrepancy-error-card__entity-name">
                      {entityName}
                    </span>
                  </div>
                )}
                {canNavigateToEntityId && entityRoute ? (
                  <Link
                    to={entityRoute.to}
                    params={entityRoute.params as any}
                    search={entityRoute.search as any}
                    className="discrepancy-error-card__entity-row discrepancy-error-card__entity-row--link"
                    onClick={onClose}
                  >
                    <span className="discrepancy-error-card__entity-label">
                      {entityIdLabel}:
                    </span>
                    <span className="discrepancy-error-card__entity-value">
                      {entityId}
                    </span>
                  </Link>
                ) : (
                  <div className="discrepancy-error-card__entity-row">
                    <span className="discrepancy-error-card__entity-label">
                      {entityIdLabel}:
                    </span>
                    <span className="discrepancy-error-card__entity-value">
                      {entityId || "—"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="discrepancy-error-card__meta-row">
              <div className="discrepancy-error-card__meta-col">
                <div className="discrepancy-error-card__meta-label">
                  Primary Record
                </div>
                <span className="discrepancy-record-badge--primary">
                  {anomaly.ReferenceRecordType}
                </span>
              </div>
              <div className="discrepancy-error-card__meta-col">
                <div className="discrepancy-error-card__meta-label">
                  Secondary Record
                </div>
                {anomaly.DiscrepancyRecordType ? (
                  <button
                    type="button"
                    className="discrepancy-record-badge--secondary"
                    onClick={handleSecondaryRecordClick}
                    disabled={!canViewRecord(anomaly.DiscrepancyRecordType)}
                  >
                    <span>{anomaly.DiscrepancyRecordType}</span>
                    <FiLink2 size={14} color="#64748B" />
                  </button>
                ) : (
                  <span className="discrepancy-record-badge--secondary">—</span>
                )}
              </div>
              <div className="discrepancy-error-card__meta-col">
                <div className="discrepancy-error-card__meta-label">
                  Program Year
                </div>
                <div className="discrepancy-error-card__meta-value">
                  {(anomaly as any).ProgramYear}
                </div>
              </div>
              <div className="discrepancy-error-card__meta-col">
                <div className="discrepancy-error-card__meta-label">
                  Preschool Code
                </div>
                <div className="discrepancy-error-card__meta-value">
                  {(anomaly as any).PreschoolCode || "—"}
                </div>
              </div>
            </div>
          </div>

          {errorDetails?.ErrorMessageDescription && (
            <div className="discrepancy-error-card__description">
              <FiInfo size={18} style={{ marginTop: "2px", flexShrink: 0 }} />
              <span>{errorDetails.ErrorMessageDescription}</span>
            </div>
          )}
        </div>

        {/* Inner Tab Bar */}
        <div className="discrepancy-tab-bar">
          <button
            type="button"
            className={`discrepancy-tab-btn${activeTab === "recordData" ? " active" : ""}`}
            onClick={() => setActiveTab("recordData")}
          >
            <FiFileText size={16} />
            <span>Primary Record</span>
          </button>

          <button
            type="button"
            className={`discrepancy-tab-btn${activeTab === "allErrors" ? " active" : ""}`}
            onClick={() => setActiveTab("allErrors")}
          >
            <FiAlertTriangle size={16} />
            <span>Related Errors</span>
            <span className="badge rounded-pill discrepancy-tab-btn__badge">
              {relatedErrors.length}
            </span>
          </button>

          <button
            type="button"
            className={`discrepancy-tab-btn${activeTab === "relatedRecords" ? " active" : ""}`}
            onClick={() => setActiveTab("relatedRecords")}
          >
            <FiLink size={16} />
            <span>Related Records</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "recordData" &&
          (canViewPrimaryRecord ? (
            <RecordDataTable
              referenceRecord={freshReferenceRecord}
              affectedFields={affectedFields}
              recordTypeName={recordTypeName}
              coreData={coreData}
              preschools={currentOrgPreschools}
              leaName={leaName}
            />
          ) : (
            <NoAccessMessage recordType={recordTypeName} />
          ))}

        {activeTab === "allErrors" && (
          <AllErrorsTab
            errorsByRecordType={errorsByRecordType}
            errorMessageMap={errorMessageMap}
            onFixRecord={handleFixRecord}
            viewPermissions={viewPermissions}
            editPermissions={editPermissions}
          />
        )}

        {activeTab === "relatedRecords" && (
          <RelatedRecordsTab
            entityId={entityId}
            currentRecordType={anomaly.ReferenceRecordType}
            secondaryRecordType={anomaly.DiscrepancyRecordType}
            csppid={(freshReferenceRecord as any)?.CSPPID ?? entityId}
            onEditRecord={(recordType, recordData) => {
              if (!canViewRecord(recordType) || !onUpdateRecord) return;
              onUpdateRecord({
                recordType: recordType as RecordType,
                recordData: recordData as any,
                entityId,
                sourceTab: "relatedRecords",
              });
            }}
            onDetailViewChange={handleRelatedRecordsDetailViewChange}
            viewPermissions={viewPermissions}
            editPermissions={editPermissions}
          />
        )}
      </div>
    </Popup>
  );
}
